import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import untar from 'js-untar';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { UserPopulatedDTO } from '../api/generated';
import { useGetPresignedDownloadUrl } from '../api/mutations/heap/useGetPresignedDownloadUrl';
import { useTriggerCapture } from '../api/mutations/heap/useTriggerCapture';
import { useHeapEvents } from '../api/useHeapEvents';
import { HeapLiveView } from '../components/Heap';
import { HeapActionBar } from '../components/Heap/HeapActionBar';
import { HeapActivityCard } from '../components/Heap/HeapActivityCard';
import {
  DetectionRowView,
  HeapActivityHeaderItem,
  HeapCaptureState,
  HeapDetection,
  HeapImageLog,
  normalizeModelClass,
} from '../components/Heap/types';
import { useSubmitComplaint } from '../api/mutations/heap/useSubmitComplaint';
import { getColorForClass } from '../components/Heap/utils';

interface HeapPageProps {
  user: UserPopulatedDTO;
}

export function HeapPage({ user }: HeapPageProps): ReactElement {
  const { t } = useTranslation();
  const token = useAuth().user?.access_token;

  const { mutateAsync: triggerCapture } = useTriggerCapture();
  const { mutateAsync: getDownloadUrl } = useGetPresignedDownloadUrl();
  const [triggerId, setTriggerId] = useState<string | undefined>(undefined);
  const [relativeTime, setRelativeTime] = useState<string>('');
  const [isFetchingAssets, setIsFetchingAssets] = useState<boolean>(false);
  const [cancelCountdown, setCancelCountdown] = useState<number | null>(null);
  const [invalidDetectionIndexes, setInvalidDetectionIndexes] = useState<number[]>([]);
  const invalidationActionStackRef = useRef<number[]>([]);

  const { cameraConnected, captureState, setCaptureState } = useHeapEvents(
    user.preferredJobsite?.id || '',
    triggerId,
    token
  );

  const { mutateAsync: submitComplaint, isPending: isSubmitComplaintPending } = useSubmitComplaint();

  const canStartCapture = cameraConnected && ['idle', 'processed', 'error'].includes(captureState.status);
  const handleStartCapture = canStartCapture
    ? async () => {
        setInvalidDetectionIndexes([]);
        invalidationActionStackRef.current = [];
        const { data } = await triggerCapture({ jobsiteId: user.preferredJobsite?.id || '' });

        setCaptureState({
          status: 'started',
        });
        setTriggerId(data?.trigger_id);
      }
    : undefined;

  const isCaptureActive = ['started', 'uploaded'].includes(captureState.status);
  const showCancel = isCaptureActive && cancelCountdown === 0;

  const handleCancelCapture = useCallback(() => {
    if (captureState.imageUrl) {
      URL.revokeObjectURL(captureState.imageUrl);
    }

    setCaptureState({ status: 'idle' });
    setTriggerId(undefined);
    setRelativeTime('');
    setCancelCountdown(null);
    setIsFetchingAssets(false);
    setInvalidDetectionIndexes([]);
    invalidationActionStackRef.current = [];
  }, [captureState.imageUrl, setCaptureState, setTriggerId, setRelativeTime, setCancelCountdown, setIsFetchingAssets]);

  const invalidDetectionIndexSet = useMemo(() => new Set(invalidDetectionIndexes), [invalidDetectionIndexes]);

  const handleInvalidateDetection = useCallback(
    (detectionIndex: number) => {
      if (invalidDetectionIndexSet.has(detectionIndex)) {
        return;
      }

      setInvalidDetectionIndexes((previous) => [...previous, detectionIndex]);
      invalidationActionStackRef.current.push(detectionIndex);
    },
    [invalidDetectionIndexSet]
  );

  const handleUndoInvalidation = useCallback(() => {
    if (invalidationActionStackRef.current.length === 0) {
      return;
    }

    const restoredDetectionIndex = invalidationActionStackRef.current.pop();
    if (restoredDetectionIndex === undefined) {
      return;
    }

    setInvalidDetectionIndexes((previousInvalidIndexes) =>
      previousInvalidIndexes.filter((index) => index !== restoredDetectionIndex)
    );
  }, []);

  const validDetections = useMemo(
    () => (captureState.imageLog?.detections || []).filter((_, index) => !invalidDetectionIndexSet.has(index)),
    [captureState.imageLog?.detections, invalidDetectionIndexSet]
  );

  const handleSubmitComplaint = useCallback(
    async (text: string) => {
      await submitComplaint({
        jobsiteId: user.preferredJobsite?.id,
        title: t('heap.complaint.title'),
        recordedAt: captureState.capturedAt!,
        datetime: {
          key: t('heap.recordedAt'),
          value: t('common.intlDateTime', {
            val: captureState.capturedAt,
            formatParams: { val: { dateStyle: 'medium', timeStyle: 'short' } },
          }),
        },
        licensePlate: {
          key: t('common.licensePlate'),
          value: captureState.imageLog?.license_plate ?? '—',
        },
        text: {
          key: t('heap.complaint.comment'),
          value: text,
        },
        detections: {
          key: t('heap.complaint.overview'),
          value: {
            displayClasses: validDetections.map((d) => [t(`heap.classes.${d.classes[0] || 'unknown'}`)]),
            bbox: validDetections.map((d) => d.bbox),
            colors: validDetections.map((d) => getColorForClass(d.classes?.[0] ?? 'unknown')),
          },
        },
      });
    },
    [
      submitComplaint,
      user.preferredJobsite?.id,
      captureState.capturedAt,
      captureState.imageLog?.license_plate,
      validDetections,
      t,
    ]
  );

  useEffect(() => {
    setInvalidDetectionIndexes([]);
    invalidationActionStackRef.current = [];
  }, [captureState.triggerId]);

  // calculate relative time since capture
  useEffect(() => {
    if (!captureState.capturedAt) {
      setRelativeTime('');
      return undefined;
    }

    const updateRelativeTime = () => {
      const now = new Date();
      const diffMs = now.getTime() - captureState.capturedAt!.getTime();
      const diffMinutes = Math.floor(diffMs / 1000 / 60);
      const diffHours = Math.floor(diffMinutes / 60);

      if (diffHours > 0) {
        setRelativeTime(`${diffHours}h ${diffMinutes % 60}m`);
      } else if (diffMinutes > 0) {
        setRelativeTime(`${diffMinutes}m`);
      } else {
        setRelativeTime('0m');
      }
    };

    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 60000);
    return () => clearInterval(interval);
  }, [captureState.capturedAt]);

  // download and process captured data
  useEffect(() => {
    if (
      captureState.status !== 'processed' ||
      !captureState.triggerId ||
      captureState.imageUrl ||
      captureState.imageLog
    ) {
      return;
    }

    let cancelled = false;
    setIsFetchingAssets(true);

    (async () => {
      try {
        const { data } = await getDownloadUrl({
          jobsiteId: user.preferredJobsite?.id || '',
          bucket: captureState.bucket!,
          object: captureState.object!,
        });
        if (!data || !data.url) {
          throw new Error('Failed to get download URL');
        }
        const { imageUrl, imageLog } = await fetchCaptureAssets(data);

        if (cancelled) return;
        setCaptureState((prev) => ({
          ...prev,
          imageUrl,
          imageLog,
        }));
      } catch (error) {
        console.error('Error processing tar file:', error);
        setCaptureState({
          status: 'error',
        });
      } finally {
        if (!cancelled) {
          setIsFetchingAssets(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setIsFetchingAssets(false);
    };
  }, [
    captureState.bucket,
    captureState.imageLog,
    captureState.imageUrl,
    captureState.object,
    captureState.status,
    captureState.triggerId,
    getDownloadUrl,
    setCaptureState,
    user.preferredJobsite?.id,
  ]);

  // countdown timer for cancel action - reset when capture starts and when processing starts
  useEffect(() => {
    if (captureState.status === 'started' || captureState.status === 'uploaded') {
      setCancelCountdown(COUNTDOWN_SECONDS);
    } else if (!isCaptureActive) {
      setCancelCountdown(null);
    }
  }, [captureState.status, isCaptureActive]);

  useEffect(() => {
    if (!isCaptureActive) return undefined;

    const interval = setInterval(() => {
      setCancelCountdown((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isCaptureActive]);

  // cleanup object URL on unmount or when imageUrl changes
  useEffect(() => {
    return () => {
      if (captureState.imageUrl) {
        URL.revokeObjectURL(captureState.imageUrl);
      }
    };
  }, [captureState.imageUrl]);

  const statusChips = getStatusChips(captureState, cameraConnected);
  const activityHeaderItems = getActivityHeaderItems(captureState, relativeTime);

  const detectionRows = getDetectionRows(validDetections);

  const isWaitingForProcessing =
    ['started', 'uploaded'].includes(captureState.status) && !captureState.imageUrl && !captureState.imageLog;
  const isLoading = isFetchingAssets;
  const hasCapture = captureState.status === 'processed' && captureState.imageUrl && captureState.imageLog;

  const actionBarContent = (
    <HeapActionBar
      onStartCapture={handleStartCapture}
      onCancelCapture={showCancel ? handleCancelCapture : undefined}
      statusChips={statusChips}
    />
  );

  const captureCardContent: ReactNode = (() => {
    if (isLoading) {
      return <CircularProgress />;
    }

    if (isWaitingForProcessing) {
      return <HourglassEmptyOutlinedIcon fontSize='large' color='action' />;
    }

    if (hasCapture && captureState.imageUrl && captureState.imageLog && captureState.capturedAt) {
      return (
        <HeapActivityCard
          imageUrl={captureState.imageUrl}
          detections={captureState.imageLog.detections}
          invalidDetectionIndexes={invalidDetectionIndexes}
          onInvalidateDetection={handleInvalidateDetection}
          onUndoInvalidation={handleUndoInvalidation}
          onSubmitComplaint={handleSubmitComplaint}
          isSubmitComplaintPending={isSubmitComplaintPending}
          headerItems={activityHeaderItems}
          detectionRows={detectionRows}
          emptyMessage={'heap.noDetections'}
          classLabel={'heap.class'}
          countLabel={'heap.count'}
          modelClassLabel={'heap.modelClass'}
        />
      );
    }

    if (handleStartCapture && canStartCapture) {
      return (
        <Button variant='outlined' size='large' onClick={handleStartCapture}>
          <Box display='flex' flexDirection='row' alignItems='center' gap={2}>
            <CameraAltIcon />
            <Typography>{t('heap.startCapture')}</Typography>
          </Box>
        </Button>
      );
    }

    return null;
  })();

  return <HeapLiveView actionBarContent={actionBarContent} captureCardContent={captureCardContent} />;
}
const COUNTDOWN_SECONDS = 10;

async function fetchCaptureAssets({ url }: { url: string }) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch tar');
  const files = await untar(await res.arrayBuffer());
  const imageFile = files.find((f) => f.name.includes('original_image.jpg'));
  const logFile = files.find((f) => f.name.includes('image_log.json'));
  if (!imageFile || !logFile) throw new Error('Missing files in tar');
  return {
    imageUrl: URL.createObjectURL(new Blob([imageFile.buffer], { type: 'image/jpeg' })),
    imageLog: JSON.parse(new TextDecoder().decode(logFile.buffer)) as HeapImageLog,
  };
}

function getStatusChips(captureState: HeapCaptureState, cameraConnected: boolean) {
  return [
    { label: 'heap.cameraStatus', config: getCameraChip(cameraConnected) },
    { label: 'heap.captureStatus', config: getCaptureChip(captureState) },
    { label: 'heap.processingStatus', config: getProcessingChip(captureState) },
  ];
}

function getCameraChip(isConnected: boolean) {
  if (isConnected) {
    return {
      icon: <CheckCircleOutlineIcon />,
      bgColor: 'success.main',
    };
  } else {
    return {
      icon: <ErrorOutlineIcon />,
      bgColor: 'error.main',
    };
  }
}

function getCaptureChip(captureState: HeapCaptureState) {
  switch (captureState.status) {
    case 'started':
      return {
        icon: <CircularProgress size='1.25rem' color='inherit' />,
        bgColor: 'warning.main',
      };
    case 'uploaded':
    case 'processed':
      return {
        icon: <CheckCircleOutlineIcon />,
        bgColor: 'success.main',
      };
    case 'error':
      return {
        icon: <ErrorOutlineIcon />,
        bgColor: 'error.main',
      };
    default:
      return {
        icon: <HourglassEmptyOutlinedIcon htmlColor='text.secondary' />,
        bgColor: 'info',
        color: 'text.secondary',
      };
  }
}

function getProcessingChip(captureState: HeapCaptureState) {
  switch (captureState.status) {
    case 'uploaded':
      return {
        icon: <CircularProgress size='1.25rem' color='inherit' />,
        bgColor: 'warning.main',
      };
    case 'processed':
      return {
        icon: <CheckCircleOutlineIcon fontSize='small' />,
        bgColor: 'success.main',
      };
    case 'error':
      return {
        icon: <ErrorOutlineIcon fontSize='small' />,
        bgColor: 'error.main',
      };
    default:
      return {
        icon: <HourglassEmptyOutlinedIcon htmlColor='text.secondary' />,
        bgColor: 'info',
        color: 'text.secondary',
      };
  }
}

function getActivityHeaderItems(captureState: HeapCaptureState, relativeTime: string): HeapActivityHeaderItem[] {
  if (!captureState.capturedAt) return [];

  const items: HeapActivityHeaderItem[] = [
    {
      icon: <AccessTimeIcon />,
      label: 'heap.relativeTime',
      value: [relativeTime || '0m'],
    },
    {
      icon: <CalendarTodayIcon />,
      label: 'heap.recordedAt',
      value: [
        'common.intlDateTime',
        {
          val: captureState.capturedAt,
          formatParams: { val: { hour: '2-digit', minute: '2-digit', second: '2-digit' } },
        },
      ],
    },
  ];

  if (captureState.imageLog?.license_plate) {
    items.push({
      icon: <LocalShippingIcon />,
      label: 'common.licensePlate',
      value: [captureState.imageLog.license_plate],
    });
  }
  return items;
}

function getDetectionRows(detections: HeapDetection[]): DetectionRowView[] {
  const classMap = new Map<string, DetectionRowView>();
  detections.forEach((detection) => {
    detection.classes.forEach((className, index) => {
      const modelClass = normalizeModelClass(detection.sources?.[index]);
      const key = `${className}::${modelClass}`;
      const existing = classMap.get(key);
      if (existing) {
        classMap.set(key, { ...existing, count: existing.count + 1 });
        return;
      }

      classMap.set(key, {
        label: `heap.classes.${className}`,
        count: 1,
        color: getColorForClass(className),
        modelClass,
      });
    });
  });

  return Array.from(classMap.values()).sort((a, b) => b.count - a.count);
}
